
import type React from "react"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Upload, FileText, X, Loader2 } from "lucide-react"
import { api } from "@/lib/api"
import { toast } from "sonner"
import { getYears, SUBJECTS, DIFFICULTY_OPTIONS, formatFileSize } from "@/lib/utils"
import { useTagManager } from "@/lib/hooks/useTagManager"
import { useFileUpload } from "@/lib/hooks/useFileUpload"
import { parseFilename } from "@/lib/parseFilename"

const years = getYears()
const subjects = SUBJECTS

export default function UploadPage() {
  const navigate = useNavigate()
  const { file, isDragging, setFile, handleFileDrop, handleFileSelect, handleDragOver, handleDragLeave } = useFileUpload()
  const { tags, tagInput, setTagInput, handleAddTag, handleRemoveTag, handleKeyDown } = useTagManager()
  const [title, setTitle] = useState("")
  const [year, setYear] = useState("")
  const [region, setRegion] = useState("")
  const [subject, setSubject] = useState("")
  const [difficulty, setDifficulty] = useState("")
  const [duration, setDuration] = useState("120")
  const [suggestion, setSuggestion] = useState("")
  const [isUploading, setIsUploading] = useState(false)

  // Auto-fill form from filename when file is selected
  useEffect(() => {
    if (file) {
      const parsed = parseFilename(file.name)

      if (parsed.title) {
        setTitle(parsed.title)
      }
      if (parsed.year) {
        setYear(parsed.year.toString())
      }
      if (parsed.region) {
        setRegion(parsed.region)
      }
      if (parsed.subject) {
        setSubject(parsed.subject)
      }

      if (parsed.year || parsed.region || parsed.subject) {
        toast.success("已自动填充表单")
      }
    }
  }, [file])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return

    setIsUploading(true)
    try {
      // First upload the file to R2
      const uploadResult = await api.uploadFile(file)

      // Then create the paper record in D1
      await api.createPaper({
        title,
        year: parseInt(year),
        region,
        subject,
        difficulty: difficulty || undefined,
        tags,
        duration: parseInt(duration),
        suggestion: suggestion || undefined,
        file_key: uploadResult.file_key,
        file_size: uploadResult.file_size
      })

      toast.success("试卷上传成功！")
      // Navigate to admin page after successful upload
      navigate("/admin")
    } catch (error) {
      console.error("Upload error:", error)
      toast.error(error instanceof Error ? error.message : "上传失败，请重试")
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    navigate("/admin")
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:h-16 sm:px-6">
          <h1 className="text-lg font-semibold text-foreground sm:text-xl">试卷管理平台</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <Card className="shadow-sm">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* File Upload Area */}
              <div className="space-y-2">
                <Label>文件上传</Label>
                <div
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`relative flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors sm:min-h-[200px] ${isDragging
                      ? "border-primary bg-primary/5"
                      : file
                        ? "border-border bg-muted/20"
                        : "border-border bg-muted/10 hover:border-primary hover:bg-primary/5"
                    }`}
                >
                  {file ? (
                    <div className="flex flex-col items-center gap-2 px-4 text-center sm:gap-3">
                      <div className="rounded-full bg-primary/10 p-3 sm:p-4">
                        <FileText className="h-6 w-6 text-primary sm:h-8 sm:w-8" />
                      </div>
                      <div>
                        <p className="break-all text-sm font-medium text-foreground sm:text-base">{file.name}</p>
                        <p className="text-xs text-muted-foreground sm:text-sm">{formatFileSize(file.size)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFile(null)
                        }}
                      >
                        重新选择
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 px-4 text-center sm:gap-3">
                      <div className="rounded-full bg-muted p-3 sm:p-4">
                        <Upload className="h-6 w-6 text-muted-foreground sm:h-8 sm:w-8" />
                      </div>
                      <div>
                        <p className="mb-1 text-sm text-foreground sm:text-base">拖拽 PDF 文件到这里，或点击选择文件</p>
                        <p className="text-xs text-muted-foreground sm:text-sm">支持 .pdf 格式，最大 50MB</p>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                </div>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">
                  试卷标题 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  placeholder="例：2019年全国II卷-数学"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              {/* Year and Region */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="year">
                    年份 <span className="text-destructive">*</span>
                  </Label>
                  <Select value={year} onValueChange={setYear} required>
                    <SelectTrigger id="year">
                      <SelectValue placeholder="选择年份" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}年
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">
                    地区 <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="region"
                    placeholder="例：全国II卷、北京卷"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">
                  科目 <span className="text-destructive">*</span>
                </Label>
                <Select value={subject} onValueChange={setSubject} required>
                  <SelectTrigger id="subject">
                    <SelectValue placeholder="选择科目" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Difficulty */}
              <div className="space-y-2">
                <Label>难度</Label>
                <RadioGroup value={difficulty} onValueChange={setDifficulty}>
                  <div className="flex flex-wrap gap-4 sm:gap-6">
                    {DIFFICULTY_OPTIONS.map((level) => (
                      <div key={level} className="flex items-center space-x-2">
                        <RadioGroupItem value={level} id={level} />
                        <Label htmlFor={level} className="font-normal">
                          {level}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label htmlFor="tags">标签</Label>
                <div className="space-y-3">
                  <Input
                    id="tags"
                    placeholder="添加标签，如：函数、导数"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleAddTag}
                  />
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="gap-1 pr-1 bg-primary/10 text-primary">
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="rounded-full p-0.5 hover:bg-primary/20"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label htmlFor="duration">建议用时</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    max="300"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">分钟</span>
                </div>
              </div>

              {/* Suggestion */}
              <div className="space-y-2">
                <Label htmlFor="suggestion">做题建议</Label>
                <Textarea
                  id="suggestion"
                  placeholder="给学生的做题建议和注意事项"
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Form Actions */}
              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full sm:w-auto bg-transparent"
                >
                  取消
                </Button>
                <Button
                  type="submit"
                  disabled={isUploading || !file || !title || !year || !region || !subject}
                  className="w-full sm:w-auto"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      上传中...
                    </>
                  ) : (
                    "上传试卷"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  )
}
